import Table from 'antd/es/table'
import type { ColumnsType, TablePaginationConfig, TableProps } from 'antd/es/table'
import type { CSSProperties, ReactNode } from 'react'
import { UiIcon, type UiIconName } from '../icons'

export type StatusTone = 'success' | 'warning' | 'error' | 'processing' | 'neutral' | 'purple'

export function StatusBadge({
  children,
  tone = 'neutral',
}: {
  children: ReactNode
  tone?: StatusTone
}) {
  return <span className={`yh-status yh-status--${tone}`}>{children}</span>
}

export function PageHeader({
  title,
  subtitle,
  actions,
  meta,
}: {
  title: ReactNode
  subtitle?: ReactNode
  actions?: ReactNode
  meta?: ReactNode
}) {
  return (
    <div className="yh-page-header">
      <div>
        <div className="yh-page-header__title-row">
          <h1>{title}</h1>
          {meta}
        </div>
        {subtitle ? <p>{subtitle}</p> : null}
      </div>
      {actions ? <div className="yh-page-header__actions">{actions}</div> : null}
    </div>
  )
}

export function Panel({
  title,
  extra,
  children,
  className = '',
}: {
  title?: ReactNode
  extra?: ReactNode
  children: ReactNode
  className?: string
}) {
  return (
    <section className={`yh-panel ${className}`.trim()}>
      {title || extra ? (
        <header className="yh-panel__header">
          <h2>{title}</h2>
          {extra}
        </header>
      ) : null}
      <div className="yh-panel__body">{children}</div>
    </section>
  )
}

export interface MetricCardProps {
  label: string
  value: ReactNode
  icon: UiIconName
  tone?: StatusTone
  note?: ReactNode
}

export function MetricCard({ label, value, icon, tone = 'processing', note }: MetricCardProps) {
  return (
    <article className="yh-metric">
      <span className={`yh-metric__icon yh-metric__icon--${tone}`}>
        <UiIcon name={icon} size={22} />
      </span>
      <div>
        <span className="yh-metric__label">{label}</span>
        <strong className="yh-metric__value">{value}</strong>
        {note ? <small>{note}</small> : null}
      </div>
    </article>
  )
}

export function MetricGrid({ children, columns = 4 }: { children: ReactNode; columns?: number }) {
  return (
    <div className="yh-metric-grid" style={{ '--metric-columns': columns } as CSSProperties}>
      {children}
    </div>
  )
}

export function InlineNotice({
  tone = 'info',
  title,
  children,
  actions,
}: {
  tone?: 'info' | 'warning' | 'error' | 'success'
  title?: ReactNode
  children: ReactNode
  actions?: ReactNode
}) {
  const icon =
    tone === 'warning'
      ? 'warning'
      : tone === 'error'
        ? 'error'
        : tone === 'success'
          ? 'check'
          : 'shield'
  return (
    <div className={`yh-notice yh-notice--${tone}`}>
      <span className="yh-notice__icon">
        <UiIcon name={icon} size={20} />
      </span>
      <div className="yh-notice__copy">
        {title ? <strong>{title}</strong> : null}
        <div>{children}</div>
      </div>
      {actions ? <div className="yh-notice__actions">{actions}</div> : null}
    </div>
  )
}

export function Toolbar({ children, actions }: { children: ReactNode; actions?: ReactNode }) {
  return (
    <div className="yh-toolbar">
      <div className="yh-toolbar__filters">{children}</div>
      {actions ? <div className="yh-toolbar__actions">{actions}</div> : null}
    </div>
  )
}

export function SectionTabs<T extends string>({
  value,
  items,
  onChange,
}: {
  value: T
  items: readonly { key: T; label: string }[]
  onChange: (key: T) => void
}) {
  return (
    <div className="yh-section-tabs" role="tablist">
      {items.map((item) => (
        <button
          key={item.key}
          role="tab"
          aria-selected={value === item.key}
          type="button"
          className={value === item.key ? 'is-active' : ''}
          onClick={() => onChange(item.key)}
        >
          {item.label}
        </button>
      ))}
    </div>
  )
}

export function ProgressBar({
  value,
  tone = 'blue',
  label,
}: {
  value: number
  tone?: 'blue' | 'green' | 'orange' | 'red'
  label?: ReactNode
}) {
  const clamped = Math.max(0, Math.min(100, value))
  return (
    <div className="yh-progress-wrap">
      <div className={`yh-progress yh-progress--${tone}`}>
        <span style={{ width: `${clamped}%` }} />
      </div>
      {label ?? <small>{clamped}%</small>}
    </div>
  )
}

export function DetailList({
  items,
  columns = 2,
}: {
  items: readonly { label: ReactNode; value: ReactNode }[]
  columns?: number
}) {
  return (
    <dl className="yh-detail-list" style={{ '--detail-columns': columns } as CSSProperties}>
      {items.map((item, index) => (
        <div key={index}>
          <dt>{item.label}</dt>
          <dd>{item.value}</dd>
        </div>
      ))}
    </dl>
  )
}

export function DataTable<T extends object>({
  columns,
  data,
  rowKey,
  pagination = false,
  rowSelection,
  size = 'middle',
  scroll,
  sticky,
  loading = false,
  emptyText = '暂无数据',
}: {
  columns: ColumnsType<T>
  data: readonly T[]
  rowKey: keyof T | ((record: T) => string)
  pagination?: false | TablePaginationConfig
  rowSelection?: TableProps<T>['rowSelection']
  size?: 'small' | 'middle' | 'large'
  scroll?: { x?: number | string; y?: number | string }
  sticky?: TableProps<T>['sticky']
  loading?: boolean
  emptyText?: ReactNode
}) {
  const resolvedRowKey =
    typeof rowKey === 'function' ? rowKey : (record: T) => String(record[rowKey])
  return (
    <Table<T>
      className="yh-data-table"
      columns={columns}
      dataSource={[...data]}
      rowKey={resolvedRowKey}
      pagination={pagination}
      rowSelection={rowSelection}
      size={size}
      scroll={scroll}
      sticky={sticky}
      loading={loading}
      locale={{ emptyText }}
    />
  )
}

export function ReadOnlyBanner({
  onDetails,
  message = '历史数据仍可查看；新增、修改、采集、生成、发布和订单同步均已停止，请联系总后台增加积分或恢复账号。',
}: {
  onDetails?: () => void
  message?: ReactNode
}) {
  return (
    <InlineNotice
      tone="warning"
      title="账号当前为只读模式"
      actions={
        onDetails ? (
          <button className="yh-link-button" type="button" onClick={onDetails}>
            查看账号详情
          </button>
        ) : undefined
      }
    >
      {message}
    </InlineNotice>
  )
}

export function EmptyState({
  title,
  description,
  action,
}: {
  title: string
  description: string
  action?: ReactNode
}) {
  return (
    <div className="yh-empty">
      <span>
        <UiIcon name="database" size={28} />
      </span>
      <h3>{title}</h3>
      <p>{description}</p>
      {action}
    </div>
  )
}
